import { z } from "zod";
import { ExternalIdEntrySchema } from "./external-id.schema";

// Service/maintenance PLAN template (ADR-0048 / ADV-1175). The reusable
// "membership type" — billing amount + frequency, visit cadence, included
// services, term length, auto-renew — stamped onto service_agreements.

export const servicePlanBillingFrequencies = [
  "monthly",
  "quarterly",
  "semiannual",
  "annual",
] as const;

export const servicePlanTermUnits = ["days", "months", "years"] as const;

export const ServicePlanSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  name: z.string().describe("Name"),
  description: z.string().nullable().describe("Description"),
  billing_amount: z.number().describe("Billing Amount"),
  billing_frequency: z
    .enum(servicePlanBillingFrequencies)
    .describe("Billing Frequency"),
  visit_cadence: z.number().int().nullable().describe("Visit Cadence"),
  included_services: z.array(z.unknown()).describe("Included Services"),
  term_length_value: z.number().int().describe("Term Length Value"),
  term_length_unit: z.enum(servicePlanTermUnits).describe("Term Length Unit"),
  auto_renew: z.boolean().describe("Auto Renew"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type ServicePlan = z.infer<typeof ServicePlanSchema>;

export const ServicePlanInsertSchema = z.object({
  name: z.string().describe("Name"),
  description: z.string().nullable().optional().describe("Description"),
  billing_amount: z.number().nonnegative().describe("Billing Amount"),
  billing_frequency: z
    .enum(servicePlanBillingFrequencies)
    .describe("Billing Frequency"),
  visit_cadence: z
    .number()
    .int()
    .positive()
    .nullable()
    .optional()
    .describe("Visit Cadence"),
  included_services: z
    .array(z.unknown())
    .optional()
    .describe("Included Services"),
  term_length_value: z.number().int().positive().describe("Term Length Value"),
  term_length_unit: z.enum(servicePlanTermUnits).describe("Term Length Unit"),
  auto_renew: z.boolean().optional().describe("Auto Renew"),
  is_active: z.boolean().optional().describe("Active"),
});

export const ServicePlanUpdateSchema = ServicePlanInsertSchema.partial();

export const ServicePlanQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  billing_frequency: z.enum(servicePlanBillingFrequencies).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  is_active: z.enum(["true", "false"]).optional(),
  search: z.string().optional().describe("Search name"),
});
